
import React from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { leadPriorityLabels, LeadPriority } from "../types";

interface LeadFormData {
  name: string;
  website: string;
  industry: string;
  size_range: string;
  job_title: string;
  priority: LeadPriority;
  notes: string;
  source: string;
  geographic_area: string;
  news_monitoring_enabled: boolean;
  corporate_phone: string;
}

interface LeadFormFieldsProps {
  formData: LeadFormData;
  onFormDataChange: (fieldName: keyof LeadFormData, value: string | LeadPriority | boolean) => void;
}

export const LeadFormFields = ({ formData, onFormDataChange }: LeadFormFieldsProps) => {
  const handleChange = (fieldName: keyof LeadFormData, value: string | LeadPriority | boolean) => {
    onFormDataChange(fieldName, value);
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Company / Lead Name *</Label>
          <Input
            id="name"
            required
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="website">Website</Label>
          <Input
            id="website"
            placeholder="example.com"
            value={formData.website}
            onChange={(e) => handleChange("website", e.target.value)}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="industry">Industry</Label>
          <Input
            id="industry"
            value={formData.industry}
            onChange={(e) => handleChange("industry", e.target.value)}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="size_range">Company Size</Label>
          <Select
            value={formData.size_range}
            onValueChange={(value) => handleChange("size_range", value)}
          >
            <SelectTrigger id="size_range">
              <SelectValue placeholder="Select company size" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="not-specified">Not specified</SelectItem>
              <SelectItem value="1-10">1-10 employees</SelectItem>
              <SelectItem value="11-50">11-50 employees</SelectItem>
              <SelectItem value="51-200">51-200 employees</SelectItem>
              <SelectItem value="201-500">201-500 employees</SelectItem>
              <SelectItem value="501-1000">501-1000 employees</SelectItem>
              <SelectItem value="1001-5000">1001-5000 employees</SelectItem>
              <SelectItem value="5001+">5001+ employees</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="job_title">Job Title</Label>
          <Input
            id="job_title"
            value={formData.job_title}
            onChange={(e) => handleChange("job_title", e.target.value)}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="priority">Priority</Label>
          <Select
            value={formData.priority}
            onValueChange={(value: LeadPriority) => handleChange("priority", value)}
          >
            <SelectTrigger id="priority">
              <SelectValue placeholder="Select priority" />
            </SelectTrigger>
            <SelectContent>
              {(Object.entries(leadPriorityLabels) as [LeadPriority, string][]).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="corporate_phone">Corporate Phone</Label>
          <Input
            id="corporate_phone"
            placeholder="e.g., +1 (555) 123-4567"
            value={formData.corporate_phone}
            onChange={(e) => handleChange("corporate_phone", e.target.value)}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="geographic_area">Geographic Area</Label>
          <Input
            id="geographic_area"
            placeholder="e.g., San Francisco Bay Area, London, etc."
            value={formData.geographic_area}
            onChange={(e) => handleChange("geographic_area", e.target.value)}
          />
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Switch
              id="news_monitoring_enabled"
              checked={formData.news_monitoring_enabled}
              onCheckedChange={(checked) => handleChange("news_monitoring_enabled", checked)}
            />
            <Label htmlFor="news_monitoring_enabled">Enable News Monitoring</Label>
          </div>
          <p className="text-sm text-muted-foreground">
            Automatically track news and updates about this lead
          </p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
        <div className="space-y-2">
          <Label htmlFor="source">Lead Source</Label>
          <Input
            id="source"
            value={formData.source}
            onChange={(e) => handleChange("source", e.target.value)}
            placeholder="LinkedIn, Referral, Website, etc."
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            rows={3}
            value={formData.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
          />
        </div>
      </div>
    </>
  );
};
